import { Link } from "react-router-dom";
import { categories, type NewsItem } from "@/mocks/content";
import { formatCount, formatViews } from "@/utils/format";

export function catOf(slug: string) {
  return categories.find((c) => c.slug === slug) || categories[0];
}

export function CategoryPill({ slug }: { slug: string }) {
  const c = catOf(slug);
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
      <i className={`${c.icon} text-[12px]`} />
      {c.name}
    </span>
  );
}

export function MetaLine({
  item,
  className = "",
}: {
  item: NewsItem;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500 ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        <i className="ri-time-line" />
        {item.time}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-eye-line" />
        {formatViews(item.views)} 阅读
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-timer-line" />
        {item.readMin} 分钟
      </span>
    </div>
  );
}

export function TagList({ tags }: { tags: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tags.map((t) => (
        <span
          key={t}
          className="rounded-md bg-background-200/70 px-2 py-0.5 text-xs text-foreground-600"
        >
          {t}
        </span>
      ))}
    </div>
  );
}

function ExclusiveMark() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary-600 px-2.5 py-0.5 text-[11px] font-semibold text-background-50">
      <i className="ri-flashlight-fill text-[11px]" />
      独家
    </span>
  );
}

/** 首页头条大卡（横向：左文右图） */
export function FeaturedCard({ item }: { item: NewsItem }) {
  const c = catOf(item.category);
  return (
    <Link
      to={`/news/${item.id}`}
      className="group block overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-shadow"
    >
      <div className="grid gap-0 md:grid-cols-2">
        <div className="relative h-52 overflow-hidden md:h-auto">
          <img
            src={item.cover}
            alt={`${item.title} 独家内幕报道配图`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {item.exclusive && (
            <span className="absolute left-3 top-3">
              <ExclusiveMark />
            </span>
          )}
        </div>
        <div className="flex flex-col justify-center p-5 md:p-7">
          <div className="mb-3 flex items-center gap-2">
            <CategoryPill slug={item.category} />
            {item.hot && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
                <i className="ri-fire-fill text-[12px]" />
                热门
              </span>
            )}
          </div>
          <h3 className="font-heading text-lg font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-2xl">
            {item.title}
          </h3>
          <p className="mt-3 hidden text-sm leading-relaxed text-foreground-600 sm:block">
            {item.lead}
          </p>
          <MetaLine item={item} className="mt-4" />
          <span className="mt-4 inline-flex w-fit items-center gap-1 text-sm font-semibold text-primary-600">
            <i className={`${c.icon} text-[15px]`} />
            查看详情
          </span>
        </div>
      </div>
    </Link>
  );
}

/** 列表行卡（左缩略图，右文字） */
export function ListRow({ item }: { item: NewsItem }) {
  return (
    <Link
      to={`/news/${item.id}`}
      className="group flex items-start gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-background-100/60"
    >
      <div className="relative h-20 w-32 shrink-0 overflow-hidden rounded-lg sm:h-24 sm:w-44">
        <img
          src={item.cover}
          alt={`${item.title} 报道配图`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {item.exclusive && (
          <span className="absolute left-1.5 top-1.5 inline-flex items-center rounded bg-primary-600 px-1.5 py-0.5 text-[10px] font-semibold text-background-50">
            独家
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center gap-2">
          <CategoryPill slug={item.category} />
        </div>
        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-base">
          {item.title}
        </h3>
        <p className="mt-1.5 hidden text-xs leading-relaxed text-foreground-600 lg:line-clamp-1">
          {item.lead}
        </p>
        <MetaLine item={item} className="mt-2" />
      </div>
    </Link>
  );
}

/** 竖向小卡（网格展示） */
export function GridCard({ item }: { item: NewsItem }) {
  return (
    <Link
      to={`/news/${item.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
    >
      <div className="relative h-40 w-full overflow-hidden sm:h-44">
        <img
          src={item.cover}
          alt={`${item.title} 栏目内容配图`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {item.exclusive && (
          <span className="absolute left-2.5 top-2.5">
            <ExclusiveMark />
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2 flex items-center justify-between">
          <CategoryPill slug={item.category} />
          {item.hot && <i className="ri-fire-fill text-accent-500" />}
        </div>
        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-[15px]">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-600">
          {item.lead}
        </p>
        <MetaLine item={item} className="mt-3" />
      </div>
    </Link>
  );
}

/** 排行条（数字 + 标题） */
export function RankRow({
  item,
  index,
}: {
  item: NewsItem;
  index: number;
}) {
  return (
    <Link
      to={`/news/${item.id}`}
      className="group flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-background-100"
    >
      <span
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
          index < 3
            ? "bg-primary-600 text-background-50"
            : "bg-secondary-200 text-secondary-800"
        }`}
      >
        {index + 1}
      </span>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-1 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-700">
          {item.title}
        </span>
        <span className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
          <span>{formatCount(item.views)} 阅读</span>
          {item.exclusive && (
            <span className="inline-flex items-center gap-0.5 text-primary-600">
              <i className="ri-flashlight-fill text-[10px]" /> 独家
            </span>
          )}
        </span>
      </span>
    </Link>
  );
}

/** 竖向 mini 卡（侧栏） */
export function MiniCard({ item }: { item: NewsItem }) {
  return (
    <Link
      to={`/news/${item.id}`}
      className="group flex items-start gap-3 rounded-lg py-2 transition-colors hover:bg-background-100"
    >
      <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-lg">
        <img
          src={item.cover}
          alt={`${item.title} 缩略图`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="min-w-0">
        <h3 className="line-clamp-2 text-[13px] font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
          {item.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-500">{item.time}</p>
      </div>
    </Link>
  );
}